import { queryAll, query } from '../utils/utils.js';

/**
 * Floating Side Navigation
 * 
 * Handles:
 * - Scroll-spy for anchor links (highlighting active section)
 * - Mobile dropdown toggle
 * - Smooth scrolling to anchors
 * - Show/hide based on scroll position
 */
export class FloatingNav {
    
    constructor() {
        this.floatingNavBlocks = queryAll('section.floating-nav-block');
        
        if (this.floatingNavBlocks.length === 0) {
            return;
        }
        
        console.log('[BHS] Initializing Floating Navigation');
        
        this.floatingNavBlocks.forEach((block) => {
            this.initBlock(block);
        });
    }
    
    initBlock(block) {
        const desktopNav = block.querySelector('.floating-nav-desktop');
        const mobileNav = block.querySelector('.floating-nav-mobile');
        const mobileTrigger = block.querySelector('.floating-nav-mobile-trigger');
        const mobileMenu = block.querySelector('.floating-nav-mobile-menu');
        const mobileCurrentLabel = block.querySelector('.floating-nav-mobile-current');
        
        // Collect all anchor links
        const allLinks = block.querySelectorAll('a[data-anchor="true"]');
        const anchorTargets = [];
        
        allLinks.forEach((link) => {
            const href = link.getAttribute('href');
            if (href && href.startsWith('#')) {
                const targetId = href.substring(1);
                const targetElement = document.getElementById(targetId);
                
                if (targetElement) {
                    anchorTargets.push({
                        link: link,
                        target: targetElement,
                        id: targetId
                    });
                }
            }
        });
        
        // Initialize mobile dropdown
        if (mobileTrigger && mobileMenu) {
            this.initMobileDropdown(mobileTrigger, mobileMenu, mobileCurrentLabel);
        }
        
        // Initialize scroll-spy for anchors
        if (anchorTargets.length > 0) {
            this.initScrollSpy(block, anchorTargets, mobileCurrentLabel);
            this.initSmoothScroll(block, anchorTargets, mobileMenu, mobileTrigger);
        }
        
        // Initialize visibility based on content bounds (pass anchorTargets)
        if (desktopNav || mobileNav) {
            this.initVisibilityTracking(block, desktopNav, mobileNav, anchorTargets);
        }
        
        // Add body class for layout adjustments
        document.body.classList.add('has-floating-nav');
    }
    
    /**
     * Initialize mobile dropdown toggle
     */
    initMobileDropdown(trigger, menu, currentLabel) {
        trigger.addEventListener('click', (e) => {
            e.preventDefault();
            const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
            
            trigger.setAttribute('aria-expanded', !isExpanded);
            menu.classList.toggle('is-open', !isExpanded);
        });
        
        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!trigger.contains(e.target) && !menu.contains(e.target)) {
                trigger.setAttribute('aria-expanded', 'false');
                menu.classList.remove('is-open');
            }
        });
        
        // Close dropdown on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                trigger.setAttribute('aria-expanded', 'false');
                menu.classList.remove('is-open');
            }
        });
    }
    
    /**
     * Initialize scroll-spy to highlight active section
     */
    initScrollSpy(block, anchorTargets, mobileCurrentLabel) {
        const updateActiveSection = () => {
            const scrollPos = window.scrollY;
            const windowHeight = window.innerHeight;
            const offset = windowHeight * 0.3; // 30% from top of viewport
            
            let activeTarget = null;
            
            // Find the section that's currently in view
            for (let i = anchorTargets.length - 1; i >= 0; i--) {
                const target = anchorTargets[i];
                const rect = target.target.getBoundingClientRect();
                const elementTop = rect.top + scrollPos;
                
                if (scrollPos >= elementTop - offset) {
                    activeTarget = target;
                    break;
                }
            }
            
            // If no target found, default to first one if we're at top
            if (!activeTarget && anchorTargets.length > 0 && scrollPos < 200) {
                activeTarget = anchorTargets[0];
            }
            
            // Update active states
            anchorTargets.forEach((target) => {
                // Find all links pointing to this target (desktop and mobile)
                const allMatchingLinks = block.querySelectorAll(`a[href="#${target.id}"]`);
                
                allMatchingLinks.forEach((link) => {
                    if (activeTarget && target.id === activeTarget.id) {
                        link.classList.add('active');
                    } else {
                        link.classList.remove('active');
                    }
                });
            });
            
            // Update mobile current label
            if (mobileCurrentLabel && activeTarget) {
                mobileCurrentLabel.textContent = activeTarget.link.textContent;
            }
        };
        
        // Run on scroll with throttle
        let ticking = false;
        window.addEventListener('scroll', () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    updateActiveSection();
                    ticking = false;
                });
                ticking = true;
            }
        });
        
        // Run on load
        updateActiveSection();
    }
    
    /**
     * Initialize smooth scrolling to anchor targets
     */
    initSmoothScroll(block, anchorTargets, mobileMenu, mobileTrigger) {
        const allAnchorLinks = block.querySelectorAll('a[data-anchor="true"]');
        
        allAnchorLinks.forEach((link) => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                
                const href = link.getAttribute('href');
                const targetId = href.substring(1);
                const targetElement = document.getElementById(targetId);
                
                if (targetElement) {
                    // Calculate offset (account for any fixed headers)
                    const headerOffset = 100; // Adjust based on your header height
                    const elementPosition = targetElement.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                    
                    // Close mobile menu after click
                    if (mobileMenu && mobileTrigger) {
                        mobileTrigger.setAttribute('aria-expanded', 'false');
                        mobileMenu.classList.remove('is-open');
                    }
                }
            });
        });
    }
    
    /**
     * Track visibility based on content bounds
     * Show the floating nav when the first content section reaches the top of the viewport
     */
    initVisibilityTracking(block, desktopNav, mobileNav, anchorTargets) {
        // Find the main content area to determine visibility zone
        const entryContent = document.querySelector('.entry-content');
        const heroBlock = document.querySelector('.hero-block, .detail-hero, section.b-hero-block');
        
        // Find the first overview cards block as a fallback for visibility start point
        const firstOverviewCard = document.querySelector('.b-overview-cards-block');
        
        // If no anchor targets found, still show the nav based on scroll position
        const hasTargets = anchorTargets && anchorTargets.length > 0;
        
        const updateVisibility = () => {
            const scrollPos = window.scrollY;
            const windowHeight = window.innerHeight;
            
            let shouldShow = false;
            
            // Determine the start point for showing the nav
            let startElement = null;
            let endElement = null;
            
            if (hasTargets) {
                // Use anchor targets if available
                startElement = anchorTargets[0].target;
                endElement = anchorTargets[anchorTargets.length - 1].target;
            }
            
            // If we have an overview card that appears before our first anchor target, use it instead
            if (firstOverviewCard) {
                const overviewTop = firstOverviewCard.getBoundingClientRect().top + scrollPos;
                const startTop = startElement ? startElement.getBoundingClientRect().top + scrollPos : Infinity;
                
                if (overviewTop < startTop) {
                    startElement = firstOverviewCard;
                }
                
                // Also find the last overview card for end point
                const allOverviewCards = document.querySelectorAll('.b-overview-cards-block');
                if (allOverviewCards.length > 0) {
                    const lastCard = allOverviewCards[allOverviewCards.length - 1];
                    const lastCardBottom = lastCard.getBoundingClientRect().top + scrollPos + lastCard.offsetHeight;
                    const endBottom = endElement ? endElement.getBoundingClientRect().top + scrollPos + endElement.offsetHeight : 0;
                    
                    if (lastCardBottom > endBottom) {
                        endElement = lastCard;
                    }
                }
            }
            
            if (startElement && endElement) {
                const startTop = startElement.getBoundingClientRect().top + scrollPos;
                const endBottom = endElement.getBoundingClientRect().top + scrollPos + endElement.offsetHeight;
                
                // Show when first section is at or near the top of viewport (account for header ~150px)
                const headerOffset = 150;
                const hideOffset = 200;
                
                shouldShow = scrollPos >= (startTop - headerOffset) && 
                            scrollPos <= (endBottom + hideOffset);
            } else {
                // Fallback - show nav after scrolling past hero, hide near footer
                const heroBottom = heroBlock ? heroBlock.getBoundingClientRect().bottom + scrollPos : 300;
                const contentBottom = entryContent ? entryContent.getBoundingClientRect().bottom + scrollPos : document.body.scrollHeight;
                
                shouldShow = scrollPos >= (heroBottom - 100) && 
                            (scrollPos + windowHeight) <= (contentBottom + 200);
            }
            
            if (shouldShow) {
                if (desktopNav) desktopNav.classList.add('is-visible');
                if (mobileNav) mobileNav.classList.add('is-visible');
                block.classList.remove('is-hidden');
            } else {
                if (desktopNav) desktopNav.classList.remove('is-visible');
                if (mobileNav) mobileNav.classList.remove('is-visible');
                block.classList.add('is-hidden');
            }
        };
        
        // Run on scroll with throttle
        let ticking = false;
        window.addEventListener('scroll', () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    updateVisibility();
                    ticking = false;
                });
                ticking = true;
            }
        });
        
        // Run on resize
        window.addEventListener('resize', updateVisibility);
        
        // Initial check after a short delay to ensure page is rendered
        setTimeout(updateVisibility, 100);
    }
}

